import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "account-and-billing",
  courseId: "account-and-security",
  order: 1,
  title: "Account and billing",
  subtitle: "Settings, plan, and feature requests",
  summary:
    "Change your name, email, badge, password, department, and light or dark theme from the one Settings page, then manage your plan and use the feature requests board.",
  learn: [
    "Update your account details and switch between Light, Dark, and System",
    "Start a trial, choose a plan, and manage billing",
    "Know which rows your department controls",
    "Post and follow requests on the feature board",
  ],
  minutes: 5,
  releasedAt: "2025-06-01T00:00:00.000Z",
  body: [
    { type: "heading", text: "Account settings and appearance" },
    {
      type: "paragraph",
      text: "Everything about your account is on **Settings**. Click the gear in the sidebar footer to open it.",
    },
    {
      type: "steps",
      items: [
        "Under **Account**, click Change Name, Change Email, Change Badge #, Change Password, or Change Department. Each opens an inline dialog.",
        "Under **Appearance**, pick Light, Dark, or System.",
        "Under **Security**, manage your sign-in methods.",
        "Click **Logout** at the bottom when you are done.",
      ],
    },
    {
      type: "paragraph",
      text: "System follows your operating system. The sidebar's **Toggle dark mode** is a quick switch for the same setting. The theme is saved in this browser, and shareable report pages always render in light mode regardless of your choice.",
    },
    {
      type: "callout",
      tone: "note",
      title: "Agency accounts",
      text: "If you belong to an agency, Email, Department, and Billing are read-only and marked \"Managed by your department\". Ask your admin to change them.",
    },
    {
      type: "paragraph",
      text: "Account deletion is available in the iOS app's settings only. On the web, contact support or your agency admin.",
    },
    { type: "heading", text: "Subscription and billing" },
    {
      type: "paragraph",
      text: "If you are not on a department plan, you pay for your own subscription. Each account gets one free trial, and after it ends you need an active plan to keep working.",
    },
    {
      type: "steps",
      items: [
        "Open /subscription, or click **Choose Plan** in **Settings** under **Account**.",
        "Pick Monthly, Semi-Annually, or Annually (\"Most Popular\").",
        "Click **Start 7-day free trial** if you have not had one, or **Choose Plan** to pay now.",
      ],
    },
    {
      type: "paragraph",
      text: "After you subscribe, the Settings row becomes **Manage Plan**. It opens the billing portal, where you change your card, see invoices, and cancel.",
    },
    {
      type: "callout",
      tone: "warning",
      title: "When the trial ends",
      text: "Every case page, upload, and export returns you to /subscription until a plan is active. Login, settings, and account deletion keep working, so you can always get back in or leave.",
    },
    {
      type: "callout",
      tone: "note",
      title: "Agency members",
      text: "If your department bought seats, your Billing row reads \"Managed by your department\" and there is nothing to pay. When an agency evaluation period ends you see \"Your agency's evaluation period concluded\". Your admin, not you, decides what happens next.",
    },
    {
      type: "callout",
      tone: "note",
      title: "iOS subscriptions",
      text: "Subscriptions bought in the iOS app go through Apple. Manage those from the phone's Subscription screen. The web **Manage Plan** button cannot change an Apple subscription.",
    },
    { type: "heading", text: "Feature requests" },
    {
      type: "paragraph",
      text: "**Feature Requests** in the sidebar is one board shared by every department, for paying customers: agency members and active personal subscribers. Trial accounts do not see it. Other officers on the board see your name and department on anything you post.",
    },
    {
      type: "bullets",
      items: [
        "Click **Feature Requests**. The first time, read the terms and click **I agree**.",
        "Search the board and upvote an existing idea, or click **New request**, fill in Title, Area, and Details, and click **Post request**.",
        "Open a request to click **Post comment** or reply to a comment, and use the bell to **Subscribe** or **Unsubscribe**.",
        "The Roadmap tab shows what is planned and in progress. The Changelog tab shows what shipped, by month.",
        "Posting, voting, or commenting subscribes you automatically. You get an email when a request you follow moves to planned, in progress, or delivered, and when staff reply. Unsubscribe once and it sticks, even if you vote again.",
      ],
    },
    {
      type: "callout",
      tone: "tip",
      text: "Describe the situation, not the button. \"I need to add a second officer's narrative to the same case\" is more useful to staff than \"add multi-author mode\".",
    },
  ],
  related: ["multi-factor-authentication", "the-agency-dashboard", "report-preferences-in-depth"],
};

export default lesson;
